import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import images from "../../assets/images";
import { ROUTES } from "../../constants/routes.constants";
import userService from "../../services/user.service";
import inService from "../../services/transfer-in-request.service";
import outService from "../../services/transfer-out-request.service";
import MessageAnotherPharmacyService from "../../services/MessageAnotherPharmacy.service";
import ViewTransferRequestModal from "../../components/ViewTransferRequestModal";
import ViewMessageAnotherPharmacyModal from "../../components/ViewMessageAnotherPharmacyModal";
import EditTransferRequestModal from "../../components/EditTransferRequestModal";
import AcceptTransferRequestModal from "../../components/AcceptTransferRequestModal";
import RejectTransferRequestModal from "../../components/RejectTransferRequestModal";
import CancelTransferRequestModal from "../../components/CancelTransferRequestModal";
import FinishReviewTransferRequestModal from "../../components/FinishReviewTransferRequestModal";
import { userSlice } from "../../redux/slices/user.slice";
import { lookupSlice } from "../../redux/slices/lookup.slice";
import lookupService from "../../services/lookup.service";
import {
  clearCodeLoginContext,
  getCodeLoginContext,
} from "../../utility/code-login-context";
import UpdateGuestInfoModal from "../../components/UpdateGuestInfoModal";
import Emitter from "../../utility/emitter";

const isTransferOutType = (notificationTypeId) =>
  [1, 3, 5].includes(Number(notificationTypeId));
const isTransferInType = (notificationTypeId) =>
  [2, 4, 6].includes(Number(notificationTypeId));
const isMessageType = (notificationTypeId) => Number(notificationTypeId) === 10;

const CodeNotificationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.currentUser);
  const user = currentUser?.user;

  const codeContext = getCodeLoginContext();
  const notificationTypeId =
    user?.notificationTypeId ?? codeContext?.notificationTypeId;
  const recordId = user?.recordId ?? codeContext?.recordId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [record, setRecord] = useState({});
  const [requestType, setRequestType] = useState(0);
  const [viewTransfer, setViewTransfer] = useState(false);
  const [viewMessage, setViewMessage] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [transferRequestModalShow, setTransferRequestModalShow] = useState(false);
  const [acceptTransferRequestModalShow, setAcceptTransferRequestModalShow] =
    useState(false);
  const [
    finishReviewTransferRequestModalShow,
    setFinishReviewTransferRequestModalShow,
  ] = useState(false);
  const [rejectTransferRequestModalShow, setRejectTransferRequestModalShow] =
    useState(false);
  const [cancelTransferRequestModalShow, setCancelTransferRequestModalShow] =
    useState(false);
  const [showUpdateGuestInfoModal, setShowUpdateGuestInfoModal] = useState(false);

  const loadRecord = async () => {
    if (!recordId) {
      setError("No notification found for this code.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      if (isMessageType(notificationTypeId)) {
        const result = await MessageAnotherPharmacyService.GetById(recordId);
        if (result?.error === null) {
          setRecord(result.data);
          setViewMessage(true);
        } else {
          setError("Unable to load this message.");
        }
      } else if (isTransferOutType(notificationTypeId)) {
        const result = await outService.GetById(recordId);
        if (result?.error === null) {
          setRequestType(1);
          setRecord(result.data);
          setViewTransfer(true);
        } else {
          setError("Unable to load this transfer request.");
        }
      } else if (isTransferInType(notificationTypeId)) {
        const result = await inService.GetById(recordId);
        if (result?.error === null) {
          setRequestType(0);
          setRecord(result.data);
          setViewTransfer(true);
        } else {
          setError("Unable to load this transfer request.");
        }
      } else {
        // Fallback: try transfer-in, then transfer-out, then message
        const inResult = await inService.GetById(recordId);
        if (inResult?.error === null) {
          setRequestType(0);
          setRecord(inResult.data);
          setViewTransfer(true);
        } else {
          const outResult = await outService.GetById(recordId);
          if (outResult?.error === null) {
            setRequestType(1);
            setRecord(outResult.data);
            setViewTransfer(true);
          } else {
            const msgResult = await MessageAnotherPharmacyService.GetById(recordId);
            if (msgResult?.error === null) {
              setRecord(msgResult.data);
              setViewMessage(true);
            } else {
              setError("Unable to load this notification.");
            }
          }
        }
      }
    } catch (err) {
      console.error("Code notification load error:", err);
      setError("Unable to load this notification.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "TransferMyRx::Notification";
    const loadLookups = async () => {
      const lookups = await lookupService.GetAll();
      if (lookups.error === null) {
        dispatch(lookupSlice.actions.getAll(lookups.data));
      }
    };
    loadLookups();
    loadRecord();
  }, [recordId, notificationTypeId]);

  useEffect(() => {
    Emitter.on("SHOW_GUEST_REGISTERATION_MODAL", () => {
      setShowUpdateGuestInfoModal(true);
    });
    return () => {
      Emitter?.off("SHOW_GUEST_REGISTERATION_MODAL");
    };
  }, []);

  const handleLogout = async () => {
    await userService.Logout();
    clearCodeLoginContext();
    dispatch(userSlice.actions.logout());
    navigate(ROUTES.LOGIN_ROUTE, { replace: true });
  };

  const handleRefresh = async (id) => {
    if (id !== undefined) {
      await loadRecord();
    }
  };

  const displayName =
    user?.firstname && user.firstname !== "Guest"
      ? user.firstname
      : user?.fullName || "Guest";

  return (
    <div className="p-3 p-sm-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <a href="https://www.TransferMyRx.com">
            <img
              src={images.Logo}
              alt="TransferMyRx"
              style={{ maxHeight: 48 }}
            />
          </a>
          <span>
            Hello, <strong>{displayName}</strong>
          </span>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-success" />
          <div className="mt-2">Loading notification...</div>
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && !viewTransfer && !viewMessage && (
        <div className="alert alert-warning" role="alert">
          No notification details available.
        </div>
      )}

      <ViewTransferRequestModal
        show={viewTransfer}
        onHide={() => setViewTransfer(true)}
        requesttype={requestType}
        request={record}
        handleEditTransferRequestModalShow={(state) => {
          setEditMode(true);
          setTransferRequestModalShow(state);
        }}
        handleAcceptTransferRequestModalShow={(state) =>
          setAcceptTransferRequestModalShow(state)
        }
        handleFinishReviewTransferRequestModalShow={(state) =>
          setFinishReviewTransferRequestModalShow(state)
        }
        handleRejectTransferRequestModalShow={(state) =>
          setRejectTransferRequestModalShow(state)
        }
        handleCancelTransferRequestModalShow={(state) =>
          setCancelTransferRequestModalShow(state)
        }
      />

      <ViewMessageAnotherPharmacyModal
        isOpen={viewMessage}
        onClose={() => setViewMessage(true)}
        record={record}
      />

      <EditTransferRequestModal
        show={transferRequestModalShow}
        onHide={(refresh, transferRequestId) => {
          setTransferRequestModalShow(false);
          if (refresh === true) {
            handleRefresh(transferRequestId);
          }
        }}
        requesttype={requestType}
        request={record}
        handleAcceptTransferRequestModalShow={(state) =>
          setAcceptTransferRequestModalShow(state)
        }
        handleFinishReviewTransferRequestModalShow={(state) =>
          setFinishReviewTransferRequestModalShow(state)
        }
        handleRejectTransferRequestModalShow={(state) =>
          setRejectTransferRequestModalShow(state)
        }
        handleCancelTransferRequestModalShow={(state) =>
          setCancelTransferRequestModalShow(state)
        }
      />

      <AcceptTransferRequestModal
        show={acceptTransferRequestModalShow}
        onHide={(refresh) => {
          setAcceptTransferRequestModalShow(false);
          if (refresh === true) {
            if (editMode) {
              setTransferRequestModalShow(false);
            }
            handleRefresh(editMode ? undefined : record.RequestId);
          }
        }}
        requesttype={requestType}
        request={record}
      />

      <FinishReviewTransferRequestModal
        show={finishReviewTransferRequestModalShow}
        onHide={(refresh) => {
          setFinishReviewTransferRequestModalShow(false);
          if (refresh === true) {
            if (editMode) {
              setTransferRequestModalShow(false);
            }
            handleRefresh(editMode ? undefined : record.RequestId);
          }
        }}
        requesttype={requestType}
        request={record}
      />

      <RejectTransferRequestModal
        show={rejectTransferRequestModalShow}
        onHide={(refresh) => {
          setRejectTransferRequestModalShow(false);
          if (refresh === true) {
            if (editMode) {
              setTransferRequestModalShow(false);
            }
            handleRefresh(editMode ? undefined : record.RequestId);
          }
        }}
        requesttype={requestType}
        request={record}
      />

      <CancelTransferRequestModal
        show={cancelTransferRequestModalShow}
        onHide={(refresh) => {
          setCancelTransferRequestModalShow(false);
          if (refresh === true) {
            if (editMode) {
              setTransferRequestModalShow(false);
            }
            handleRefresh(editMode ? undefined : record.RequestId);
          }
        }}
        requesttype={requestType}
        request={record}
      />

      <UpdateGuestInfoModal
        show={showUpdateGuestInfoModal}
        onHide={() => setShowUpdateGuestInfoModal(false)}
      />
    </div>
  );
};

export default CodeNotificationPage;
